import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import { selectPreviousRoute } from 'applications/common/stores/router/selectors.js';
import InPlayTypes from 'modules/inplay/types.js';

const EventUtils = MojitoServices.SportsContent.Events.utils;
const SportsMenu = MojitoModules.SportsMenu.module;
const QuickEventsNavigation = MojitoModules.QuickEventsNavigation;
const EventVisualisationSkeleton = MojitoModules.EventVisualisationSkeleton;
const EventNotificationSubscriber = MojitoModules.EventNotifications.EventNotificationSubscriber;
const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const AnalyticsContext = MojitoCore.Presentation.AnalyticsContext.Context;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;
const { getSeparatorTranslationKey } = MojitoPresentation.Utils.ParticipantSeparator;
const { Visualisation } = MojitoModules;
const { module: EventMarkets } = MojitoModules.EventMarkets;

const {
    FlexPane,
    DocumentHead,
    EventDisclaimer,
    GlobalLinks,
    PageHeader,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    LoaderSuspense,
    EmptyContent,
    QuickNavigationButton,
    QuickNavigationPane,
    Button,
    Text,
    ParticipantIconsInline,
    scoreboardUtils: { sortParticipants },
} = MojitoPresentation.Components;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const IntentTypes = MojitoPresentation.Base.Intent.Types;
const NavigationPathBuilder = MojitoPresentation.Base.Navigation.NavigationPathBuilder;
const intentActions = MojitoCore.Intents.actions;

const { FAVORITES } = InPlayTypes.TABS;

const DEFAULT_REDIRECT_DELAY_AFTER_EVENT_ENDED_MS = 30000;
const DEFAULT_HOME_PAGE_INTENT = {
    type: IntentTypes.VIEW,
    data: NavigationPathBuilder.toHome(),
};
const DEFAULT_EVENT_END_REDIRECT_INTENT = {
    type: IntentTypes.VIEW,
    data: NavigationPathBuilder.toInplay(),
};
const { useLocalizedParams } = MojitoPresentation.Hooks;
const { useEventItem, useEventItemUnavailability } = MojitoModules.Common.Hooks;
const {
    actions: userSettingsActions,
    types: UserSettingsTypes,
    selectors: userSettingsSelectors,
} = MojitoServices.UserSettings;
const { selectOnboarding } = userSettingsSelectors;
const {
    selectors: { selectFavoriteEvents, selectShouldReturnToFavoritesTab },
    slice: { actions: favoritesActions },
} = MojitoServices.Favorites;
const { EVENTS_QUICK_NAVIGATION } = UserSettingsTypes.ONBOARDING_TYPES;

const analyticsContext = new AnalyticsContextBuilder()
    .withAnalyticsPath('Event Detail page')
    .build();

const isInPlayEvent = event => Boolean(event.details.inPlay);

const EventPage = props => {
    const dispatch = useDispatch();
    const { mojitoTools } = props;
    const { config } = mojitoTools;
    const { eventId } = useLocalizedParams();
    const eventItem = useEventItem(eventId);
    const [eventItemState, setEventItemState] = useState(eventItem);
    const isEventItemUnavailable = useEventItemUnavailability(eventId);
    const commonProps = { ...props, eventItem };
    const delayBeforeRedirect =
        config.redirectDelayAfterEventEnded ?? DEFAULT_REDIRECT_DELAY_AFTER_EVENT_ENDED_MS;

    // Event is just ended in case if we have eventItem saved in state and eventItem from store was deleted.
    const isEventJustEnded = !!eventItemState && eventItemState?.details.inPlay && !eventItem;

    useEffect(() => {
        if (eventItem) {
            setEventItemState(eventItem);
        }
    }, [eventItem]);

    useEffect(() => {
        dispatch(favoritesActions.fetchFavoriteEventIds());
        return () => {
            // reset shouldReturnToFavoritesTab after leaving the page
            dispatch(favoritesActions.setShouldReturnToFavoritesTab(false));
        };
    }, [dispatch]);

    useEffect(() => {
        const eventEndRedirectIntent =
            config.eventEndRedirectIntent ?? DEFAULT_EVENT_END_REDIRECT_INTENT;
        const { type, data } = eventEndRedirectIntent;
        if (isEventJustEnded && type && data) {
            const timer = setTimeout(() => {
                dispatch(intentActions.publishIntent(type, data));
            }, delayBeforeRedirect);
            return () => clearTimeout(timer);
        }
    }, [isEventJustEnded, dispatch, delayBeforeRedirect, config.eventEndRedirectIntent]);

    return (
        <>
            <EventDocumentHead {...commonProps} />
            <PageSplitLayout>
                {/* We show EmptyContent when event doesn't exist (wrong URL) or if it was ended long before and is unavailable,
                that's why it shouldn't be just ended and it should be unavailable. */}
                {!isEventJustEnded && isEventItemUnavailable ? (
                    <EmptyContent config={config.emptyContent} />
                ) : (
                    <>
                        <EventPageHeader {...commonProps} eventItem={eventItemState} />

                        {isEventJustEnded ? (
                            <EndedEventContent {...commonProps} eventItemState={eventItemState} />
                        ) : (
                            <EventContent {...commonProps} />
                        )}
                    </>
                )}
                <SecondaryContent {...commonProps} />
            </PageSplitLayout>
        </>
    );
};

const EndedEventContent = props => {
    const dispatch = useDispatch();
    const {
        mojitoTools: { config, stringResolver },
        eventItemState,
    } = props;

    const onClick = () => {
        const homePageIntent = config.homePageIntent ?? DEFAULT_HOME_PAGE_INTENT;
        const { type, data } = homePageIntent;
        if (type && data) {
            dispatch(intentActions.publishIntent(type, data));
        }
    };
    const headerText = stringResolver.resolveString('$EVENT_PAGE.EVENT_CARD_CAROUSEL_HEADER');

    return (
        <>
            <VisualisationContent {...props} eventItem={eventItemState} />
            <FlexPane config={config.infoCardWrapper} class="ta-EndedEventInfoCard">
                <FlexPane config={config.infoCardContent}>
                    <FlexPane config={config.textContainer}>
                        <Text class="ta-EndedEventText" config={config.text}>
                            {stringResolver.resolveString('$EVENT_PAGE.INFO_CARD.TEXT')}
                        </Text>
                    </FlexPane>
                    <Button class="ta-EndedEventButton" config={config.button} onClick={onClick}>
                        <Text config={config.buttonLabel}>
                            {stringResolver.resolveString('$EVENT_PAGE.INFO_CARD.BUTTON_LABEL')}
                        </Text>
                    </Button>
                </FlexPane>
            </FlexPane>
            <EventCardsCarousel
                config={config.eventCardsCarousel}
                pageId={'homepage'}
                headerText={headerText}
            />
        </>
    );
};

const EventContent = props => {
    const { eventItem, mojitoTools } = props;
    const { config } = mojitoTools;

    return (
        <FlexPane config={config.eventContainer} class="ta-EventContent">
            <VisualisationContent {...props} />
            <EventMarkets config={config.eventMarkets} eventId={eventItem?.id} />
            {eventItem && (
                <EventDisclaimer sportId={eventItem.sportId} isInPlay={eventItem.details.inPlay} />
            )}
        </FlexPane>
    );
};

const VisualisationContent = props => {
    const { mojitoTools, eventItem } = props;
    const { config } = mojitoTools;

    const skeleton = <EventVisualisationSkeleton config={config.visualisationSkeleton} />;

    return (
        <LoaderSuspense
            config={config.visualisationSkeletonLoader}
            isContentPending={!eventItem}
            loader={skeleton}
        >
            <FlexPane config={config.visualisationContainer} class="ta-EventVisualisationContainer">
                {eventItem && <Visualisation event={eventItem} />}
            </FlexPane>
        </LoaderSuspense>
    );
};

const EventNotificationButton = ({ eventId }) => (
    <AnalyticsContext.Provider value={analyticsContext}>
        <EventNotificationSubscriber eventId={eventId} />
    </AnalyticsContext.Provider>
);

const SecondaryContent = props => {
    const { eventItem, mojitoTools } = props;
    const { config } = mojitoTools;
    const { sportId } = eventItem || {};

    return (
        <FlexPane config={config.secondaryContentContainer} class="ta-EventSecondaryContent">
            <SportsMenu selectedSportId={sportId} />
            <GlobalLinks />
        </FlexPane>
    );
};

const EventDocumentHead = props => {
    const { eventItem, mojitoTools } = props;
    const { config, appContext, l10n } = mojitoTools;

    if (!eventItem) {
        return null;
    }

    const isInPlay = isInPlayEvent(eventItem);

    const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;
    const documentHeadMetaFactory = new metaFactory(l10n);
    const { name, id, className, classId, sportId, typeName, typeId, startTime } = eventItem;
    const sportName = sportId && appContext.sportName(sportId);
    const key = isInPlay ? 'INPLAY' : 'PREPLAY';

    const headData = {
        title: `$EVENT_PAGE.PAGE_TITLE.${key}`,
        keywords: `$EVENT_PAGE.PAGE_KEYWORDS.${key}`,
        description: `$EVENT_PAGE.PAGE_DESCRIPTION.${key}`,
        canonical: `$EVENT_PAGE.PAGE_CANONICAL.${key}`,
        localizedParams: new Map([
            ['sportName', sportName],
            ['eventName', name],
            ['eventClass', className],
            ['eventType', typeName],
            ['eventStartTime', startTime],
        ]),
        params: {
            sportId,
            eventId: id,
            eventClassId: classId,
            eventClassType: typeId,
        },
    };

    return <DocumentHead data={headData} metaFactory={documentHeadMetaFactory} />;
};

const EventPageHeader = props => {
    const dispatch = useDispatch();
    const { isInplayPage, eventItem, mojitoTools } = props;
    const onboarding = useSelector(selectOnboarding);
    const previousRoute = useSelector(selectPreviousRoute);
    const favoriteEvents = useSelector(selectFavoriteEvents);
    const shouldReturnToFavoritesTab = useSelector(selectShouldReturnToFavoritesTab);

    const {
        config,
        stringResolver,
        appContext: { analyticsEmitter },
    } = mojitoTools;
    const { [EVENTS_QUICK_NAVIGATION]: quickNavigationOnboarding } = onboarding;
    const [isQuickNavigationPopupOpen, setIsQuickNavigationPopupOpen] = useState(false);

    const sortedParticipants = useMemo(
        () => sortParticipants(eventItem?.participants, eventItem?.homeAway),
        [eventItem?.participants, eventItem?.homeAway]
    );

    const {
        sportId,
        typeId,
        id: eventId,
        className,
        typeName,
        name: eventName,
        eventType,
        details: eventDetails,
    } = eventItem || {};

    const selectedTab = useMemo(
        () =>
            shouldReturnToFavoritesTab && favoriteEvents.length && isInplayPage
                ? FAVORITES
                : sportId,
        [favoriteEvents, sportId, shouldReturnToFavoritesTab, isInplayPage]
    );

    if (config.pageHeader.hide) {
        return null;
    }
    const closePopup = () => {
        analyticsEmitter.emitAnalytics('eventsQuickNavigationClosed');
        setIsQuickNavigationPopupOpen(false);
    };

    const onTooltipClick = () => {
        dispatch(
            userSettingsActions.updateOnboardingProgress({
                [EVENTS_QUICK_NAVIGATION]: { passed: true },
            })
        );
    };

    const onQuickNavigationClick = () => {
        setIsQuickNavigationPopupOpen(true);
        analyticsEmitter.emitAnalytics('eventsQuickNavigationOpened', {
            sportId,
            eventType,
            eventName,
            isInPlay: eventDetails?.inPlay,
        });

        if (!quickNavigationOnboarding?.passed) {
            onTooltipClick();
        }
    };

    const isOutrightPage = EventUtils.isOutrightEventType(eventType);
    const tooltipText =
        !quickNavigationOnboarding?.passed && !isOutrightPage
            ? stringResolver.resolveString('$EVENT_PAGE.QUICK_NAVIGATION_TOOLTIP')
            : undefined;

    const navigateInsideSportsbook = () => {
        if (isOutrightPage) {
            IntentActionTrigger.showSportOutrights(sportId, typeId, false);
        } else {
            isInplayPage
                ? IntentActionTrigger.showInPlay(sportId)
                : IntentActionTrigger.showSportCompetitions(sportId, typeId, false);
        }
    };

    const subTitleContent = !isOutrightPage ? (
        <ParticipantIconsInline
            participants={sortedParticipants}
            sportId={eventItem?.sportId}
            separator={stringResolver.resolveString(
                getSeparatorTranslationKey(eventItem?.homeAway)
            )}
            config={config.participantsIconInline}
        />
    ) : undefined;

    const onPageHeaderBackClicked = () => {
        previousRoute ? window.history.back() : navigateInsideSportsbook();
    };

    return (
        <FlexPane config={config.pageHeaderContainer} class="ta-EventPageHeader">
            {eventItem && (
                <PageHeader
                    showBackButton={true}
                    config={config.pageHeaderConfig}
                    onBackClicked={onPageHeaderBackClicked}
                    tooltipText={tooltipText}
                    onTooltipClick={onTooltipClick}
                >
                    <QuickNavigationButton
                        class="ta-EventQuickNavigationButton"
                        disabled={!eventDetails?.inPlay && isOutrightPage}
                        config={config.quickNavigationButton}
                        title={stringResolver.resolveAndFormatString(
                            '$EVENT_PAGE.QUICK_NAVIGATION_BUTTON_TITLE',
                            className,
                            typeName
                        )}
                        subTitle={eventName}
                        subTitleContent={subTitleContent}
                        onClick={onQuickNavigationClick}
                    />

                    <QuickNavigationPane
                        config={config.quickNavigationPane}
                        isEnabled={isQuickNavigationPopupOpen}
                        onOutsideClick={closePopup}
                    >
                        <QuickEventsNavigation
                            class="ta-EventQuickNavigation"
                            config={config.quickEventsNavigation}
                            sportId={selectedTab}
                            typeId={typeId}
                            isInPlay={isInplayPage}
                            selectedItemId={eventId}
                            onItemClick={closePopup}
                        />
                    </QuickNavigationPane>
                    <EventNotificationButton eventId={eventItem.id} />
                </PageHeader>
            )}
        </FlexPane>
    );
};

export default EventPage;
